import type { EChartsOption } from 'echarts'
import type { TooltipComponentFormatterCallbackParams } from 'echarts'
import type { ParsedTable, ChartGeneratorSettings } from '../types'
import {
  toNumber,
  axisPercentFormatter,
  DEFAULT_COLORS,
  GeneratorError,
  chartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  CHART_GRID_BOTTOM,
} from './helpers'
import { filterTableData } from '../utils/chartFilters'

/**
 * Generates ECharts options for a Scatter chart
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings (selectedRows, selectedColumns, dataType)
 * @returns ECharts option configuration object for a scatter chart
 * @throws {GeneratorError} If filtering removes all data
 */
export function scatterOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  const filteredTable = filterTableData(t, settings?.selectedRows, settings?.selectedColumns)

  const xAxisCategories = filteredTable.headers.slice(1) // X-axis categories
  const seriesNames = filteredTable.rows.map((row) => row[0] || 'Unnamed')

  // Build series data - each series is a row
  const series: echarts.ScatterSeriesOption[] = filteredTable.rows.map((row, rowIndex) => {
    const data = xAxisCategories.map((_, colIndex) => {
      const dataIndex = colIndex + 1
      const value = dataIndex < row.length ? toNumber(row[dataIndex]) : null
      return value !== null ? [colIndex, value] : null
    }).filter((point): point is [number, number] => point !== null)

    return {
      name: seriesNames[rowIndex],
      type: 'scatter',
      data: data,
      symbolSize: 8,
      emphasis: {
        focus: 'series',
      },
    }
  })

  const hasData = series.some((s) => s.data && s.data.length > 0)
  if (!hasData) {
    throw new GeneratorError('No valid numeric data found after filtering.')
  }

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (Array.isArray(params)) return ''
        const p = params as any
        const xIndex = p.value?.[0] ?? 0
        const yValue = p.value?.[1]
        const xLabel = xAxisCategories[xIndex] || 'Unknown'
        const formattedValue =
          yValue !== null && yValue !== undefined
            ? settings?.dataType === 'freq'
              ? yValue.toLocaleString()
              : `${(yValue * 100).toFixed(1)}%`
            : 'N/A'
        return `${p.seriesName}<br/>${xLabel}: ${formattedValue}`
      },
    },
    legend: chartLegend(seriesNames),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: CHART_GRID_BOTTOM,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: xAxisCategories,
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        rotate: 30,
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
      min: 0,
    },
    series: series,
    color: DEFAULT_COLORS,
  }
}
